'use client';

import { createContext, useEffect, useState, useContext } from 'react';
import type { ReactNode } from 'react';
import { getDefaultTheme } from '@pyranger/utils';

interface ThemeContext {
    theme?: string;
    changeTheme: (nextTheme?: string) => void;
}

const Context = createContext<ThemeContext>({
    changeTheme: () => undefined,
});

/**
 * This provider uses React context to store the current color theme. When the page renders, it will
 * run an effect that attempts to set the initial theme from the user's OS settings. After that, the
 * theme state is saved in localStorage so that it persists across refreshes and browser tabs.
 * @returns a context provider whose value includes the current theme and a method for changing the
 * theme
 */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [theme, setTheme] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (theme === undefined) {
            setTheme(getDefaultTheme());
        } else {
            localStorage.setItem('theme', theme);
        }
    }, [theme]);

    const changeTheme = (nextTheme?: string) => {
        if (nextTheme) {
            setTheme(nextTheme);
        } else {
            setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
        }
    };

    return (
        <Context.Provider value={{ theme, changeTheme }}>
            {children}
        </Context.Provider>
    );
};

type UseTheme = () => ThemeContext;

export const useTheme: UseTheme = () => useContext(Context);
